"use client";
import { useFormContext } from "react-hook-form";
import { RSVPStepsEnum, RSVPStepsFormInputData } from "../validators";
import { useEffect, useState } from "react";
import { Button } from "~/components/v3/themed/Button";
import { DialogTitle } from "~/components/v3/themed/Dialog";
import ThemedAutocomplete from "~/components/v3/themed/ThemedAutoComplete";
import BuyTicketStep, { formatPrice } from "./BuyTicketStep";
import { getRSVPStepsAndPageData } from "../actions/rsvp-steps-and-data";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import { cn } from "~/utils/fontsConfig";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/v3/shadcn/Tooltip";
import { FaInfoCircle } from "react-icons/fa";
import { If } from "~/components/v3/Conditional";

const BuyMembershipOrTicket = ({
    steps,
    setIsNextDisabled,
    onNext,
    membershipList,
    eventTicketPackage,
    hasManagerPermission,
    eventPrice,
    eventResourceTags,
}: {
    steps: RSVPStepsEnum[];
    setIsNextDisabled: (disabled: boolean) => void;
    onNext: () => void;
    membershipList: Awaited<
        ReturnType<typeof getRSVPStepsAndPageData>
    >["membershipList"];
    eventTicketPackage?: Awaited<
        ReturnType<typeof getRSVPStepsAndPageData>
    >["eventInfo"]["eventTicketPackage"];
    hasManagerPermission?: boolean;
    eventPrice?: string;
    eventResourceTags?: string[];
}) => {
    const form = useFormContext<RSVPStepsFormInputData>();

    const [selectedCard, setSelectedCard] = useState<
        "membership" | "ticket" | undefined
    >();
    const [membershipPrice, setMembershipPrice] = useState<string>("");
    const [membershipDescription, setMembershipDescription] =
        useState<string>("");
    const [membershipProductDescription, setMembershipProductDescription] =
        useState<string | undefined>();
    const [ticketProductDescription, setTicketProductDescription] = useState<
        string | undefined
    >();

    // reset state when coming to this step
    useEffect(() => {
        if (eventResourceTags && eventResourceTags.length > 0) {
            const membershipForEvent = membershipList?.find((m) =>
                m.tags?.some((t) => eventResourceTags.includes(t)),
            );

            if (membershipForEvent) {
                form.setValue("selectedMembership", membershipForEvent.id);
                setMembershipPrice(
                    formatPrice(
                        membershipForEvent.priceDollars,
                        membershipForEvent.priceCents,
                    ),
                );
                setMembershipDescription(membershipForEvent.description || "");
                setMembershipProductDescription(
                    membershipForEvent.membershipProductDescription || "",
                );
                setTicketProductDescription(
                    membershipForEvent.oneOffTicketProductDescription || "",
                );
            }
        } else {
            form.setValue("selectedMembership", undefined);
        }

        form.setValue("ticket", undefined);
        form.setValue("payOnce", undefined);
    }, []);

    const handleMembershipSelection = () => {
        setSelectedCard("membership");
        form.setValue("selectedMembership", membershipList?.[0]?.id);
        form.setValue("ticket.selectedTierId", "");
        form.setValue("payOnce", undefined);
        setIsNextDisabled(false);
    };

    if (steps.includes("MEMBERSHIP_REQUIRED")) {
        if (!membershipList?.length) {
            throw new Error(
                "Membership list is empty. Verify the steps builder.",
            );
        }
        return (
            <div className="flex flex-col items-start gap-4">
                <div className="flex w-full flex-col gap-4">
                    <DialogTitle className="text-white">
                        Select a membership plan
                    </DialogTitle>
                    <div className="flex w-full flex-col items-center gap-4">
                        <ThemedAutocomplete
                            idKey="id"
                            valueKey={["name"]}
                            placeholder="Select a membership plan"
                            onSelected={(e) => {
                                form.setValue("selectedMembership", e?.id);
                            }}
                            selected={membershipList[0]}
                            data={membershipList}
                            className="w-full disabled:font-bold"
                        />
                        <p>
                            {
                                membershipList.find(
                                    (m) =>
                                        m.id ===
                                        form.watch("selectedMembership"),
                                )?.description
                            }
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (steps.includes("BUY_TICKET")) {
        return (
            <BuyTicketStep
                setIsNextDisabled={setIsNextDisabled}
                eventTicketPackage={eventTicketPackage}
                hasManagerPermission={hasManagerPermission}
            />
        );
    }
    if (!!eventPrice) {
        return (
            <div className="flex flex-col items-start gap-4">
                <div className="flex w-full flex-col items-center gap-4">
                    <div className="flex w-full flex-col gap-4">
                        <DialogTitle className="text-white">
                            Select a membership plan
                        </DialogTitle>

                        <ThemedAutocomplete
                            idKey="id"
                            valueKey={["name"]}
                            placeholder="Select a membership plan"
                            onSelected={(e) => {
                                form.setValue("selectedMembership", e?.id);
                                form.setValue("ticket", undefined);
                                setIsNextDisabled(false);
                            }}
                            data={membershipList}
                            className="w-full disabled:font-bold"
                        />
                        <p>
                            {
                                membershipList?.find(
                                    (m) =>
                                        m.id ===
                                        form.watch("selectedMembership"),
                                )?.description
                            }
                        </p>
                    </div>

                    <div className="my-4 flex w-full items-center justify-center">
                        <div className="w-full flex-grow border-t border-gray-300"></div>
                        <span className="flex-shrink px-4 text-sm tracking-wide text-gray-600">
                            or
                        </span>
                        <div className="w-full flex-grow border-t border-gray-300"></div>
                    </div>

                    <div className="flex w-full flex-col gap-4">
                        <DialogTitle className="text-white">
                            Buy a one time ticket
                        </DialogTitle>

                        <Button
                            onClick={() => {
                                form.setValue("payOnce", true);
                                setIsNextDisabled(false);
                                onNext();
                            }}
                            className="w-full rounded"
                            variant="secondary"
                        >
                            Buy ticket for {eventPrice}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-start gap-4">
            <div className="flex w-full flex-col items-center gap-4 md:flex-row">
                <If
                    condition={
                        !steps.includes("MEMBERSHIP_ATTENDANCE_LIMIT_REACHED")
                    }
                >
                    <Card
                        className={cn(
                            "flex h-full w-full flex-col p-4 md:h-[300px] md:w-1/2",
                            selectedCard === "membership" &&
                                "border-2 border-v3-C100",
                        )}
                    >
                        <div className="flex h-full w-full flex-col">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 pb-2 text-v3-C100">
                                    Membership
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="cursor-help">
                                                    <FaInfoCircle size={16} />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                This community has{" "}
                                                {membershipList?.length || 0}{" "}
                                                different memberships by
                                                location.
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </CardTitle>
                                <div className="flex items-center gap-2"></div>
                                <CardTitle className="text-white">
                                    {membershipPrice}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-grow">
                                <If condition={!!membershipProductDescription}>
                                    <CardDescription className="gap-2 text-sm text-gray-400">
                                        <div>Billed per month</div>
                                        <div className="mt-4">
                                            {membershipProductDescription}
                                        </div>
                                    </CardDescription>
                                </If>
                                <If condition={!membershipProductDescription}>
                                    <CardDescription className="">
                                        {membershipDescription}
                                    </CardDescription>
                                </If>
                            </CardContent>
                            <CardFooter className="mt-auto">
                                <Button
                                    className="w-full"
                                    variant={
                                        selectedCard === "membership"
                                            ? "default"
                                            : "secondary"
                                    }
                                    onClick={handleMembershipSelection}
                                >
                                    {selectedCard === "membership"
                                        ? "Selected"
                                        : "Select"}
                                </Button>
                            </CardFooter>
                        </div>
                    </Card>
                </If>

                <BuyTicketStep
                    setIsNextDisabled={setIsNextDisabled}
                    eventTicketPackage={eventTicketPackage}
                    hasManagerPermission={hasManagerPermission}
                    isMembershipBypassTicket={true}
                    selectedCard={selectedCard}
                    setSelectedCard={setSelectedCard}
                    ticketProductDescription={ticketProductDescription}
                    isMembershipPurchaseDisabled={steps.includes(
                        "MEMBERSHIP_ATTENDANCE_LIMIT_REACHED",
                    )}
                />
            </div>
        </div>
    );
};

export default BuyMembershipOrTicket;
